import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { categoriaReceitaAdapter } from "@/data";
import { toast } from "@/hooks/use-toast";
import type { CategoriaReceita } from "@/types/tabelas-auxiliares";
import { categoriaReceitaSchema } from "@/schemas/tabelasAuxiliaresSchemas";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  item: CategoriaReceita | null;
  onSuccess: () => void;
}

export function CategoriaReceitaFormDialog({ open, onOpenChange, mode, item, onSuccess }: Props) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(categoriaReceitaSchema),
  });

  useEffect(() => {
    if (open) {
      if (mode === "edit" && item) {
        reset({ nome: item.nome, descricao: item.descricao || "" });
      } else {
        reset({ nome: "", descricao: "" });
      }
    }
  }, [open, mode, item, reset]);

  const onSubmit = async (data: any) => {
    try {
      if (mode === "create") {
        await categoriaReceitaAdapter.create(data);
      } else if (item) {
        await categoriaReceitaAdapter.update(item.id_categoria_receita, data);
      }
      onSuccess();
    } catch (error) {
      toast({ title: "Erro", description: "Não foi possível salvar.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nova Categoria de Receita" : "Editar Categoria de Receita"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label>Nome *</Label>
            <Input {...register("nome")} required />
            {errors.nome && <p className="text-sm text-destructive mt-1">{errors.nome.message as string}</p>}
          </div>
          <div>
            <Label>Descrição</Label>
            <Textarea {...register("descricao")} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit">Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
